// Pure, framework-free presentation model for Franchise Administration (design §14, slice A1).
// Everything here is a derivation over the EXISTING `getComCodes` response -- no new read
// endpoint, no fetching, no React.
//
// VOCABULARY TRANSLATION HAPPENS HERE, AND ONLY HERE. The API payload still carries the legacy
// (inverted) names:
//   franchise.locations[]        -> FACILITIES (datamart.dim_facility -- the reporting grain)
//   facility.facilities[]        -> SITES      (app.facility_site -- the old "fields" grain)
// Every component downstream of this module speaks Franchise / Facility / Site and never touches
// a raw payload key. The original object is kept on `.raw` so later write slices (A3+) can hand
// the untranslated shape back to the endpoints, which still use the old contract.

// ---------------------------------------------------------------------------------------------
// Sites
// ---------------------------------------------------------------------------------------------
// getComCodes FABRICATES a placeholder site when a facility has zero rows in app.facility_site:
//   if (facilitiesForLocation.length == 0) { ...push({ facility_site_id: 0, ... }) }
// so `facilities.length` is NEVER 0 and an unconfigured facility would otherwise report "1 site".
// Per decision #10 the exclusion lives in exactly ONE place -- here. No component re-filters.
export const SYNTHETIC_SITE_ID = 0;

export function isSyntheticSite(site) {
  return site != null && site.facility_site_id === SYNTHETIC_SITE_ID;
}

export function realSites(location) {
  const sites = (location && location.facilities) || [];
  return sites.filter((site) => !isSyntheticSite(site));
}

// ---------------------------------------------------------------------------------------------
// BDM inheritance
// ---------------------------------------------------------------------------------------------
// Deliberately mirrors the shipped Slice 4b chip logic in LocationRow so the redesigned grid and
// the classic row agree exactly during side-by-side verification. Order matters: "no effective
// value at all" wins over everything, then an inactive direct assignment, then a direct one.
export const BDM_SOURCE = {
  DIRECT: "direct",
  DIRECT_INACTIVE: "direct-inactive",
  INHERITED: "inherited",
  NONE: "none",
};

export const BDM_SOURCE_LABEL = {
  [BDM_SOURCE.DIRECT]: "Direct",
  [BDM_SOURCE.DIRECT_INACTIVE]: "Direct · Inactive",
  [BDM_SOURCE.INHERITED]: "Inherited",
  [BDM_SOURCE.NONE]: "No BDM",
};

export function resolveBdmSource(location) {
  const facilityBdmId = location.facility_bdm_id ?? null;
  const effectiveBdmName = location.effective_bdm_name ?? null;

  if (effectiveBdmName == null) return BDM_SOURCE.NONE;
  if (facilityBdmId != null && location.facility_bdm_active === 0) return BDM_SOURCE.DIRECT_INACTIVE;
  if (facilityBdmId != null) return BDM_SOURCE.DIRECT;
  return BDM_SOURCE.INHERITED;
}

// ---------------------------------------------------------------------------------------------
// Ownership collection (Multi-BDM slice 3) -- READ ONLY
// ---------------------------------------------------------------------------------------------
// `getComCodes` now returns a richer `ownership` object per facility and per franchise, produced by
// the API's single resolver (warehouse-seasons-api/scripts/lib/ownershipResolver.js). This is
// PRESENTATION ONLY in slice 3: nothing here feeds a write payload, and there is no path from any
// component to either assignment table. The scalar `facility_bdm_id` remains the only writable
// facility ownership field until the slice 4 assignment write API exists.

// Mirrors the API's OWNERSHIP_SOURCE / RESOLUTION_STATUS. Duplicated rather than shared because the
// frontend and the Functions API are separate packages with no shared module.
export const OWNERSHIP_SOURCE = {
  FACILITY_ASSIGNMENT: "facility_assignment",
  FACILITY_SCALAR: "facility_scalar",
  FRANCHISE_ASSIGNMENT: "franchise_assignment",
  FRANCHISE_LEGACY: "franchise_legacy",
  NONE: "none",
};

export const RESOLUTION_STATUS = {
  RESOLVED: "resolved",
  LEGACY_UNRESOLVED: "legacy_unresolved",
};

function normalizeOwner(owner) {
  return {
    bdmId: owner.bdm_id ?? null,
    name: owner.display_name ?? null,
    active: owner.active ?? null,
    isPrimary: Boolean(owner.is_primary),
    // EXPLICIT, never inferred from a null bdmId: a legacy free-text value such as
    // "Gene and Gibson" that has never been linked to a person in app.lksi_bdm.
    isUnresolved: owner.resolution_status === RESOLUTION_STATUS.LEGACY_UNRESOLVED,
  };
}

// Reconstructs the ownership object from the pre-resolver fields. Not dead code: it keeps the grid
// correct if the frontend is ever served against an API build without the resolver, and it keeps
// every existing test fixture (which predates `ownership`) rendering the same way.
function fallbackOwnership(location) {
  const facilityBdmId = location.facility_bdm_id ?? null;
  const franchiseBdmName = location.franchise_bdm_name ?? null;

  if (facilityBdmId != null) {
    return {
      owners: [{
        bdm_id: facilityBdmId,
        display_name: location.facility_bdm_name ?? null,
        active: location.facility_bdm_active ?? null,
        is_primary: true,
        resolution_status: RESOLUTION_STATUS.RESOLVED,
      }],
      is_inherited: false,
      source: OWNERSHIP_SOURCE.FACILITY_SCALAR,
      effective_bdm_name: location.facility_bdm_name ?? null,
    };
  }
  if (franchiseBdmName != null && String(franchiseBdmName).trim() !== "") {
    return {
      // The legacy franchise default is free text, so it is UNRESOLVED by construction -- exactly
      // how the API's resolver represents it.
      owners: [{
        bdm_id: null,
        display_name: franchiseBdmName,
        is_primary: true,
        resolution_status: RESOLUTION_STATUS.LEGACY_UNRESOLVED,
      }],
      is_inherited: true,
      source: OWNERSHIP_SOURCE.FRANCHISE_LEGACY,
      effective_bdm_name: franchiseBdmName,
    };
  }
  return { owners: [], is_inherited: false, source: OWNERSHIP_SOURCE.NONE, effective_bdm_name: null };
}

export function normalizeOwnership(location) {
  const payload = location.ownership || fallbackOwnership(location);
  const owners = (payload.owners || []).map(normalizeOwner);
  const primary = owners.find((owner) => owner.isPrimary) || null;
  const source = payload.source || OWNERSHIP_SOURCE.NONE;
  const isFacilityGrain =
    source === OWNERSHIP_SOURCE.FACILITY_ASSIGNMENT || source === OWNERSHIP_SOURCE.FACILITY_SCALAR;

  return {
    owners,
    primary,
    // Projection of `primary`, taken from the API when present so the two can never disagree.
    primaryName: payload.effective_bdm_name ?? (primary ? primary.name : null),
    isInherited: Boolean(payload.is_inherited),
    source,
    isFacilityGrain,
    ownerCount: owners.length,
    // Everyone who is NOT the primary -- what the grid summarizes as "+N".
    additionalCount: Math.max(owners.length - 1, 0),
    unresolvedNames: owners.filter((owner) => owner.isUnresolved).map((owner) => owner.name),

    // GUARD for the slice 3 editor. True only when the facility's own ownership is a COLLECTION of
    // assignments, which the single-select scalar editor cannot represent: saving through it would
    // silently collapse joint ownership to one person. Never true from the backfill (which writes
    // one assignment per scalar) -- it exists for hand-resolved joint ownership.
    isUneditableCollection:
      source === OWNERSHIP_SOURCE.FACILITY_ASSIGNMENT && owners.length > 1,
  };
}

// Franchise-grain ownership. Structurally identical to the facility object (same fields, same
// meanings) so no consumer has to understand two ownership types -- but `isInherited` is always
// false here, because a franchise has nothing to inherit from.
export function normalizeFranchiseOwnership(comcodeObj) {
  if (comcodeObj.ownership) return normalizeOwnership(comcodeObj);

  const name = comcodeObj.LKSIAccountRepName ?? null;
  const blank = name == null || String(name).trim() === "";

  return normalizeOwnership({
    ownership: blank
      ? { owners: [], is_inherited: false, source: OWNERSHIP_SOURCE.NONE, effective_bdm_name: null }
      : {
          owners: [{
            bdm_id: null,
            display_name: name,
            is_primary: true,
            resolution_status: RESOLUTION_STATUS.LEGACY_UNRESOLVED,
          }],
          is_inherited: false,
          source: OWNERSHIP_SOURCE.FRANCHISE_LEGACY,
          effective_bdm_name: name,
        },
  });
}

// ---------------------------------------------------------------------------------------------
// Needs Attention presets (design §5)
// ---------------------------------------------------------------------------------------------
// All predicates are FACILITY-grain and evaluated against a normalized facility.
//
// `severity` separates a genuine gap from a normal-but-worth-filtering state:
//   'warn' -> counts toward a franchise band's attention summary
//   'info' -> filterable, but NOT a problem. Inheriting the franchise default is the designed
//             behaviour for the large majority of facilities (170 of 211 at the Slice 2
//             verification), so counting it as "needs attention" would flag nearly the whole
//             fleet and make the band's warning meaningless.
//
// NO "no-sites" PRESET. Having no Site rows yet is a NEUTRAL state, not an actionable gap: as of
// 2026-07-21 app.facility_site holds one row across 316 facilities, so flagging it would warn on
// ~315 of 316 facilities and drown out the real ownership signals. That sparseness reflects the
// classic UI (an unsaved placeholder looked identical to a saved Site, and the save affordance was
// hidden behind an edit) rather than any problem with the grain itself -- Sites remain a valid
// part of the product and the grid still shows a neutral `siteCount` summary.
export const ATTENTION_PRESETS = [
  {
    id: "no-bdm",
    label: "No BDM",
    severity: "warn",
    predicate: (facility) => facility.bdmSource === BDM_SOURCE.NONE,
  },
  {
    id: "inactive-bdm",
    label: "Inactive BDM",
    severity: "warn",
    predicate: (facility) => facility.bdmSource === BDM_SOURCE.DIRECT_INACTIVE,
  },
  {
    id: "inherited-bdm",
    label: "Inheriting default",
    severity: "info",
    predicate: (facility) => facility.bdmSource === BDM_SOURCE.INHERITED,
  },
  {
    id: "flags-unset",
    label: "PaceSetter/NLL not set",
    severity: "info",
    predicate: (facility) => facility.pacesetter == null && facility.nextLevelLeader == null,
  },
];

const presetsById = (ids) => ATTENTION_PRESETS.filter((preset) => ids.includes(preset.id));

// ---------------------------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------------------------
export function normalizeFacility(location) {
  const sites = realSites(location);

  const facility = {
    id: location.dim_facilityID,
    name: location.location,
    isActive: location.locationactive === 1,
    email: location.locationLKSIEmail ?? null,
    notes: location.locationLKSINotes ?? null,

    facilityBdmId: location.facility_bdm_id ?? null,
    facilityBdmName: location.facility_bdm_name ?? null,
    facilityBdmActive: location.facility_bdm_active ?? null,
    franchiseBdmName: location.franchise_bdm_name ?? null,
    effectiveBdmName: location.effective_bdm_name ?? null,
    bdmSource: resolveBdmSource(location),
    // Read-only richer ownership (multi-BDM slice 3). `bdmSource` above is unchanged and still
    // drives the existing chip and the attention presets, so this is purely additive.
    ownership: normalizeOwnership(location),

    // Tri-state: 1 = Yes, 0 = No, null = not configured. The null-vs-0 distinction is meaningful
    // and must survive to the UI (a facility that is explicitly "No" is configured; null is not).
    pacesetter: location.pacesetter ?? null,
    nextLevelLeader: location.next_level_leader ?? null,

    sites,
    siteCount: sites.length,
    // `facility_source` is required by facilitysite-add-update when creating a site. getComCodes
    // emits it as `locationsource` (the com code), so surface it here rather than making the
    // dialog reach into `.raw`.
    source: location.locationsource ?? null,

    raw: location,
  };

  facility.attention = ATTENTION_PRESETS
    .filter((preset) => preset.predicate(facility))
    .map((preset) => preset.id);
  facility.warnCount = ATTENTION_PRESETS
    .filter((preset) => preset.severity === "warn" && facility.attention.includes(preset.id))
    .length;

  return facility;
}

export function normalizeFranchise(comcodeObj) {
  const facilities = (comcodeObj.locations || []).map(normalizeFacility);

  return {
    comcode: comcodeObj.comcode,
    comId: comcodeObj.comID,
    dimFranchiseId: comcodeObj.dim_franchiseID,
    // franchise_name is occasionally absent in the payload; the com code is the stable identity.
    name: comcodeObj.franchise_name || comcodeObj.comcode,
    isActive: comcodeObj.isActive === 1,
    // Free text today; becomes a bdm_id-resolved collection once the multi-BDM assignment write
    // API ships (which absorbed the former Track B bdm_id migration).
    defaultBdmName: comcodeObj.LKSIAccountRepName ?? null,
    ownership: normalizeFranchiseOwnership(comcodeObj),
    notes: comcodeObj.lkNotes ?? null,
    // Warehouse-sourced and READ-ONLY -- there is no writable franchise email (design §1.1).
    sourceEmail: comcodeObj.email ?? null,

    facilities,
    facilityCount: facilities.length,
    attentionCount: facilities.filter((facility) => facility.warnCount > 0).length,

    raw: comcodeObj,
  };
}

export function normalizeFleet(comcodes) {
  return (comcodes || []).map(normalizeFranchise);
}

// ---------------------------------------------------------------------------------------------
// Filtering (design §5)
// ---------------------------------------------------------------------------------------------
// Applied in a fixed order: active -> presets -> search. A franchise left with no visible
// facility is dropped entirely; a franchise that still has one keeps its band, so franchise
// context is never lost.
//
// Multiple presets combine with OR (a facility matching ANY selected preset is shown) -- the
// useful behaviour for triage, where the question is "show me everything that needs a look".
// Search and the active toggle combine with AND.
//
// ACTIVE FILTER SEMANTICS (design §12.3, approved change): facilities are filtered on their own
// active flag ONLY. The classic screen additionally dropped any facility whose SITES were all
// inactive, which could make a facility vanish for a reason no longer visible once sites left the
// grid. Site gaps are surfaced by the "No sites" preset instead.
export function filterFleet(franchises, options = {}) {
  const { query = "", activeOnly = true, presetIds = [] } = options;
  const q = query.trim().toLowerCase();
  const selected = presetsById(presetIds);

  const result = [];

  for (const franchise of franchises) {
    if (activeOnly && !franchise.isActive) continue;

    let facilities = franchise.facilities;

    if (activeOnly) {
      facilities = facilities.filter((facility) => facility.isActive);
    }

    if (selected.length > 0) {
      facilities = facilities.filter((facility) =>
        selected.some((preset) => facility.attention.includes(preset.id))
      );
    }

    if (q !== "") {
      const franchiseMatches =
        String(franchise.comcode).toLowerCase().includes(q) ||
        String(franchise.name).toLowerCase().includes(q);

      // A franchise hit keeps the whole group; a facility hit narrows to the matching facilities.
      if (!franchiseMatches) {
        facilities = facilities.filter((facility) =>
          String(facility.name || "").toLowerCase().includes(q)
        );
      }
    }

    if (facilities.length === 0) continue;

    // Recompute BOTH derived counts over the visible facilities so the band never mixes filtered
    // and unfiltered numbers (e.g. "1 Facility" next to "2 need attention").
    result.push({
      ...franchise,
      facilities,
      facilityCount: facilities.length,
      attentionCount: facilities.filter((facility) => facility.warnCount > 0).length,
    });
  }

  return result;
}

// Preset counts are computed over the ACTIVE-filtered fleet only -- deliberately not narrowed by
// the query or by other selected presets. Standard faceted-filter behaviour: the chip totals stay
// stable while typing, and selecting one preset does not make the others appear to empty out.
export function countAttention(franchises, options = {}) {
  const { activeOnly = true } = options;
  const counts = {};

  for (const preset of ATTENTION_PRESETS) counts[preset.id] = 0;

  for (const franchise of franchises) {
    if (activeOnly && !franchise.isActive) continue;
    for (const facility of franchise.facilities) {
      if (activeOnly && !facility.isActive) continue;
      for (const id of facility.attention) counts[id] += 1;
    }
  }

  return counts;
}

export function summarizeFleet(franchises) {
  return {
    franchiseCount: franchises.length,
    facilityCount: franchises.reduce((total, franchise) => total + franchise.facilities.length, 0),
  };
}
